/**
 * Execution statistics — everything the execution model *derives* rather than
 * stores (design spec §4 and §11.5). Pure: takes plain data and callbacks,
 * returns plain objects; no DOM, no CRDT access. The bridge assembles the
 * inputs (`getExecutionStats` in crdt-bridge.js) and views read the result.
 *
 * Vocabulary: a *period* is a doc note; it runs from the note's `created` to
 * the next note's `created` (the last period is open-ended and measured up to
 * `today`). Entries come from execution-map.js, removed ones included.
 */

import { estimateToDays } from "../core/estimate.js";
import { executionKey, localDateString } from "./execution-map.js";

const DAY_MS = 86_400_000;

function toDate(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dayString(date) {
  return localDateString(date);
}

/** ISO week label "YYYY-Www" for a YYYY-MM-DD string. */
export function isoWeekOf(dayStr) {
  const [y, m, d] = dayStr.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((date - yearStart) / DAY_MS + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

/**
 * Build the ordered list of periods from doc notes.
 * @returns {Array<{noteId, start: Date, end: Date|null, days: number}>}
 */
export function buildPeriods(docNotes, today = new Date()) {
  const notes = (docNotes || [])
    .filter((n) => n && n.id && toDate(n.created))
    .map((n) => ({ noteId: n.id, start: toDate(n.created) }))
    .sort((a, b) => a.start - b.start || String(a.noteId).localeCompare(String(b.noteId)));
  return notes.map((p, i) => {
    const end = i + 1 < notes.length ? notes[i + 1].start : null;
    const until = end || today;
    const days = Math.max(1, Math.round((until - p.start) / DAY_MS) || 1);
    return { ...p, end, days };
  });
}

/**
 * @param {object} input
 * @param {Array<object>} input.entries      all execution entries (removed included)
 * @param {Array<object>} input.docNotes     doc notes with `id` and `created`
 * @param {(nodeId: string) => object|null} input.getNode
 * @param {(node: object) => boolean} input.isDone
 * @param {(node: object) => string|null} [input.doneAt]  ISO date of completion (Done tag setAt)
 * @param {(nodeId: string) => string|null} [input.getBranchId]  top-level branch of a node
 * @param {string} [input.today]  YYYY-MM-DD
 * @param {number} [input.starvationPeriods]  pinned items unticked for this many periods are "starved"
 */
export function computeExecutionStats({
  entries = [],
  docNotes = [],
  getNode = () => null,
  isDone = () => false,
  doneAt = () => null,
  getBranchId = () => null,
  today = localDateString(),
  starvationPeriods = 3,
} = {}) {
  const todayDate = toDate(`${today}T12:00:00`) || new Date();
  const periods = buildPeriods(docNotes, todayDate);
  const periodIndex = new Map(periods.map((p, i) => [p.noteId, i]));
  const live = entries.filter((e) => periodIndex.has(e.noteId));

  // ---- per period: items and per-node lookup
  const byPeriod = new Map(periods.map((p) => [p.noteId, []]));
  const itemAt = new Map(); // `${noteId}/${nodeId}` -> entry
  for (const e of live) {
    byPeriod.get(e.noteId).push(e);
    itemAt.set(executionKey(e.noteId, e.nodeId), e);
  }
  for (const list of byPeriod.values()) list.sort((a, b) => a.order - b.order || a.addedAt.localeCompare(b.addedAt));

  // ---- per node
  const nodes = new Map();
  const touch = (nodeId) => {
    if (!nodes.has(nodeId)) {
      const node = getNode(nodeId);
      nodes.set(nodeId, {
        nodeId,
        firstEntered: null,
        lastWorked: null,
        workedDays: 0,
        workedDates: new Set(),
        periodsListed: 0,
        pinned: false,
        estimateDays: node ? estimateToDays(node.estimate) : null,
        done: node ? isDone(node) : false,
        doneAt: node ? doneAt(node) : null,
        branchId: getBranchId(nodeId),
      });
    }
    return nodes.get(nodeId);
  };
  for (const e of live) {
    const n = touch(e.nodeId);
    if (!n.firstEntered || e.addedAt < n.firstEntered) n.firstEntered = e.addedAt;
    n.periodsListed++; // Removal leaves the fact that it was listed intact.
    for (const d of e.workedOn) n.workedDates.add(d);
    if (e.pinned && !e.removedAt) n.pinned = true;
  }
  for (const n of nodes.values()) {
    n.workedDays = n.workedDates.size;
    n.lastWorked = n.workedDays ? [...n.workedDates].sort().at(-1) : null;
    n.overrunDays = n.estimateDays !== null ? Math.max(0, n.workedDays - n.estimateDays) : null;
    n.closedWithoutWork = n.done && n.periodsListed > 0 && n.workedDays === 0;
    n.workedDates = [...n.workedDates].sort();
  }

  // ---- carried-without-work: consecutive periods (ending at the current one) listed and unticked
  const current = periods.at(-1) || null;
  const carriedWithoutWork = new Map();
  if (current) {
    for (const e of byPeriod.get(current.noteId)) {
      if (e.removedAt || e.postponed) continue;
      let count = 0;
      for (let i = periods.length - 1; i >= 0; i--) {
        const item = itemAt.get(executionKey(periods[i].noteId, e.nodeId));
        if (!item || item.removedAt || item.postponed || item.workedOn.length) break;
        count++;
      }
      carriedWithoutWork.set(e.nodeId, count);
    }
  }

  // ---- per period: load and allocation
  const periodStats = periods.map((p) => {
    const items = byPeriod.get(p.noteId).filter((e) => !e.removedAt && !e.postponed);
    let remaining = 0;
    let estimated = 0;
    const byBranch = new Map();
    for (const e of items) {
      const n = nodes.get(e.nodeId);
      const est = n?.estimateDays ?? 1; // spec: default 1 day only where a value is needed
      if (n?.estimateDays !== null && n?.estimateDays !== undefined) estimated++;
      // Current load reflects work so far. Historical load stops at the next
      // period, so later ticks/completion do not rewrite a past period's load.
      const worked = n ? n.workedDates.filter((d) => p.end ? d < dayString(p.end) : d <= today).length : 0;
      const completionDate = n?.doneAt ? toDate(n.doneAt) : null;
      const completed = n?.done && (!p.end || (completionDate && completionDate < p.end));
      remaining += completed ? 0 : Math.max(0, est - worked);
      const b = n?.branchId || "(none)";
      if (!byBranch.has(b)) byBranch.set(b, { items: 0, ticks: 0 });
      byBranch.get(b).items++;
      byBranch.get(b).ticks += e.workedOn.length;
    }
    return {
      noteId: p.noteId, start: p.start, end: p.end, days: p.days,
      items,
      load: { remainingDays: remaining, periodDays: p.days, ratio: remaining / p.days, itemsWithEstimate: estimated, itemsTotal: items.length },
      allocation: Object.fromEntries(byBranch),
    };
  });

  // ---- per ISO week: ticks and items by branch, completions
  const weeks = new Map();
  const week = (w) => {
    if (!weeks.has(w)) weeks.set(w, { week: w, ticks: 0, ticksByBranch: Object.create(null), done: 0 });
    return weeks.get(w);
  };
  for (const n of nodes.values()) {
    const b = n.branchId || "(none)";
    // A task worked on in two notes on the same day still has one worked day.
    for (const d of n.workedDates) {
      const w = week(isoWeekOf(d));
      w.ticks++;
      w.ticksByBranch[b] = (w.ticksByBranch[b] || 0) + 1;
    }
  }
  for (const n of nodes.values()) {
    if (n.done && n.doneAt) week(isoWeekOf(n.doneAt.slice(0, 10))).done++;
  }

  // ---- starvation: pinned, on the current list, unticked for N+ periods
  const starvation = current
    ? byPeriod.get(current.noteId)
        .filter((e) => !e.removedAt && e.pinned && !nodes.get(e.nodeId)?.done && (carriedWithoutWork.get(e.nodeId) || 0) >= starvationPeriods)
        .map((e) => ({ nodeId: e.nodeId, periodsWithoutWork: carriedWithoutWork.get(e.nodeId) }))
    : [];

  const inExecution = new Set(current ? byPeriod.get(current.noteId).filter((e) => !e.removedAt && !e.postponed).map((e) => e.nodeId) : []);

  return {
    periods: periodStats,
    currentPeriod: periodStats.at(-1) || null,
    nodes: Object.fromEntries([...nodes.entries()].map(([id, n]) => [id, { ...n, carriedWithoutWork: carriedWithoutWork.get(id) || 0 }])),
    weeks: [...weeks.values()].sort((a, b) => a.week.localeCompare(b.week)),
    starvation,
    inExecution,
  };
}
